import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { useHasFeature } from "@/auth/access-features";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import { BackButton } from "@/components/common/BackButton";
import { LoadMore } from "@/components/common/LoadMore";
import { NoBackend } from "@/components/common/NoBackend";
import { FilterChip } from "@/components/design/FilterChip";
import { buttonClasses } from "@/components/design/button-classes";
import {
  ListingsDocument,
  SortOrder,
  type ListingFilterInput,
  type ListingState,
} from "@/graphql/generated/operations";
import { allOf } from "@/lib/collection-filter";
import { usePagedQuery } from "@/lib/use-paged-query";
import { userErrorMessage } from "@/lib/user-error";
import { ListingCard, type ListingTile } from "./ListingCard";
import { ListingReferenceSearch } from "./ListingReferenceSearch";
import { useListingReferenceSearch } from "./listing-reference";
import { LISTING_STATE_LABELS, LISTING_STATES } from "./listing-state";

// SHARE · Ausschreibungen. Alles, was genesis dem Betrachter als Ausschreibung zeigt: die eigenen,
// die, an denen er eingetragen ist, und offene, die mit einer seiner Gruppen geteilt sind. Welche
// das sind, entscheidet allein genesis (ListingVisibilityScope); die Seite filtert nicht selbst.
//
// Der Zustandsfilter geht an genesis und nicht über die geladene Seite: genesis kennt den WIRKSAMEN
// Zustand (eine offene Ausschreibung nach ihrer Frist ist geschlossen), und nur so stimmen Filter und
// Blättern zusammen. Ebenso die Suche nach einer Kennziffer; mit einem Zustand zusammen gilt beides.

type Filter = "all" | ListingState;

export function ListingsPage() {
  const intl = useIntl();
  const canApply = useHasFeature(FEATURE_CATALOG.shareApplications.key);
  const [filter, setFilter] = useState<Filter>("all");
  const search = useListingReferenceSearch();
  const input = useMemo(() => {
    const state: ListingFilterInput | null = filter === "all" ? null : { state: { eq: filter } };
    return {
      order: { createdAt: SortOrder.Desc },
      ...(state || search.where ? { where: allOf<ListingFilterInput>(search.where, state) } : {}),
    };
  }, [filter, search.where]);
  const paged = usePagedQuery<ListingTile>(ListingsDocument, {
    connectionKey: "listings",
    input,
    pageSize: 24,
    skip: search.impossible,
  });
  // Übersprungen hält Apollo das letzte Ergebnis fest. Zu einer Eingabe, die keine Kennziffer sein kann,
  // gibt es aber keins.
  const { nodes, loading, error, hasMore, loadingMore, loadMoreFailed, loadMore } = search.impossible
    ? { ...paged, nodes: [], error: undefined, hasMore: false }
    : paged;

  return (
    <main className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10" data-graphql="Listings">
      <div className="mb-5">
        <BackButton />
      </div>
      <h1 className="smycles-page-title">
        <FormattedMessage id="share.listings.title" defaultMessage="Ausschreibungen" />
      </h1>
      <p className="mt-4 max-w-md text-smycles-body text-ink">
        <FormattedMessage
          id="share.listings.subtitle"
          defaultMessage="Stellen, Programme und Aufrufe, die du verwaltest oder lesen darfst."
        />
      </p>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Link to="/share/listings/new" className={buttonClasses({ variant: "primary", size: "compact" })}>
          <FormattedMessage id="share.listings.create" defaultMessage="Neue Ausschreibung" />
        </Link>
        {canApply ? (
          <Link to="/share/open-listings" className={buttonClasses({ variant: "secondary", size: "compact" })}>
            <FormattedMessage id="share.listings.applicantView" defaultMessage="Zur Bewerberansicht" />
          </Link>
        ) : null}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <ListingReferenceSearch search={search} />
        <div className="flex flex-wrap items-center gap-2">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>
            <FormattedMessage id="share.listings.filterAll" defaultMessage="Alle" />
          </FilterChip>
          {LISTING_STATES.map((state) => (
            <FilterChip key={state} active={filter === state} onClick={() => setFilter(state)}>
              {intl.formatMessage(LISTING_STATE_LABELS[state])}
            </FilterChip>
          ))}
        </div>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="text-smycles-label text-ink-muted" aria-busy="true">
            <FormattedMessage id="common.loading" defaultMessage="Laden…" />
          </div>
        ) : error ? (
          <p role="alert" className="text-smycles-label text-ink-muted">
            {userErrorMessage(intl, error)}
          </p>
        ) : nodes.length === 0 ? (
          <NoBackend
            label={intl.formatMessage(emptyLabel({ searching: search.active, filtered: filter !== "all" }))}
            hint={
              filter === "all" && !search.active
                ? intl.formatMessage({
                    id: "share.listings.emptyHint",
                    defaultMessage: "lege eine neue Ausschreibung an",
                  })
                : undefined
            }
          />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2 @lg:grid-cols-4">
              {nodes.map((listing, index) => (
                <ListingCard idPrefix={`share-listingcard-${listing.id}`} variant="feed" key={listing.id} listing={listing} index={index} />
              ))}
            </div>
            <LoadMore hasMore={hasMore} loading={loadingMore} paused={loadMoreFailed} onLoadMore={loadMore} />
          </>
        )}
      </div>
    </main>
  );
}

/**
 * Was die leere Liste sagt. Bei einer Suche mit Zustand nennt sie den Zustand mit, damit niemand eine
 * Kennziffer für unbekannt hält, deren Ausschreibung nur in einem anderen Zustand steht.
 */
function emptyLabel({ searching, filtered }: { searching: boolean; filtered: boolean }) {
  if (searching) {
    return filtered
      ? {
          id: "share.listings.referenceSearch.emptyFiltered",
          defaultMessage: "Keine Ausschreibung mit dieser Kennziffer in diesem Zustand",
        }
      : { id: "share.listings.referenceSearch.empty", defaultMessage: "Keine Ausschreibung mit dieser Kennziffer" };
  }
  return filtered
    ? { id: "share.listings.emptyFiltered", defaultMessage: "Keine Ausschreibung in diesem Zustand" }
    : { id: "share.listings.empty", defaultMessage: "Noch keine Ausschreibung" };
}
